import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DatePicker } from './date-picker';

// Wraps <DatePicker> with prev/next chevrons that shift the ISO date by ±1 day.
// `value` is the ISO string ('YYYY-MM-DD'); `onChange` receives the new ISO string.
export const DateStepper = ({ value, onChange, maxDate, minDate, testidPrefix = 'date-stepper', disabled, className }) => {
  const toISO = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const atMax = maxDate && value === toISO(maxDate);
  const atMin = minDate && value === toISO(minDate);

  const shift = (days) => {
    if (!value) return;
    // Parse the ISO string as a local date (avoid UTC drift from `new Date(iso)`).
    const [y, m, d] = value.split('-').map(Number);
    const next = new Date(y, m - 1, d);
    next.setDate(next.getDate() + days);
    if (maxDate && next > maxDate) return;
    if (minDate && next < minDate) return;
    onChange(toISO(next));
  };

  const btnBase =
    'flex items-center justify-center px-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

  return (
    <div className="flex items-stretch gap-0 rounded-lg overflow-hidden border-0">
      <button
        type="button"
        onClick={() => shift(-1)}
        disabled={atMin || disabled}
        className={`${btnBase} rounded-l-lg -mr-px`}
        aria-label="Previous day"
        title="Previous day"
        data-testid={`${testidPrefix}-prev`}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <DatePicker
          value={value}
          onChange={onChange}
          maxDate={maxDate}
          disabled={disabled}
          className={className}
        />
      </div>
      <button
        type="button"
        onClick={() => shift(1)}
        disabled={atMax || disabled}
        className={`${btnBase} rounded-r-lg -ml-px`}
        aria-label="Next day"
        title="Next day"
        data-testid={`${testidPrefix}-next`}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

// Month + Year stepper: 12 months with wrap-around that also increments/decrements the year.
// `year`/`month` (1-12) are the state; `onChange({ year, month })` fires on step.
export const MonthStepper = ({ year, month, onMonthChange, onYearChange, minYear, maxYear, testidPrefix = 'month-stepper' }) => {
  const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const shift = (delta) => {
    let m = month + delta;
    let y = year;
    while (m < 1) { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    if (minYear !== undefined && y < minYear) return;
    if (maxYear !== undefined && y > maxYear) return;
    if (y !== year) onYearChange(y);
    onMonthChange(m);
  };

  const btnBase =
    'flex items-center justify-center px-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

  return (
    <div className="flex items-stretch gap-0 rounded-lg overflow-hidden border-0">
      <button
        type="button"
        onClick={() => shift(-1)}
        className={`${btnBase} rounded-l-lg -mr-px`}
        aria-label="Previous month"
        title="Previous month"
        data-testid={`${testidPrefix}-prev`}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <select
        value={month}
        onChange={(e) => onMonthChange(parseInt(e.target.value, 10))}
        className="flex-1 px-4 py-2 border border-gray-300 focus:ring-2 focus:ring-[#156ACA] focus:outline-none bg-white text-gray-900"
        data-testid={`${testidPrefix}-select`}
      >
        {MONTH_LABELS.map((label, idx) => (
          <option key={idx + 1} value={idx + 1}>{label}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => shift(1)}
        className={`${btnBase} rounded-r-lg -ml-px`}
        aria-label="Next month"
        title="Next month"
        data-testid={`${testidPrefix}-next`}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};
